import { fetchProductCareCopy as fetchContenfulProductCareCopy } from '@api/graphqlContentFul'
import { getValueFromObject } from '@helpers/utils'
import {
  BenefitsProps,
  ContentfulDocument,
  StatementSectionProps
} from 'types/contentful/graphQlApi'
import {
  fetchContentfulProductsBreakdownById,
  fetchContentfulProductsBreakdownByProductCategory
} from '@api/graphqlContentFul/productsBreakdown'
import { ProductsBreakdownProps } from '@components/ui/ProductsBreakdown'
import { CONTENTFUL_HEADERS_ID } from '@consts/index'
import { searchDataByAttribute } from '@helpers/contentful'
import {
  DiscountByProductType,
  fetchProductDiscount
} from '@lib/productPageUtils'
import logBuildError from '@helpers/misc/logBuildError'

const handleProductsBuildError = logBuildError('products')

async function getProductsBreakdown(
  productType: string,
  productCategory: string,
  preview = false
) {
  if (productType === 'Gift Card') {
    const { productsBreakdown } = (await fetchContentfulProductsBreakdownById({
      id: CONTENTFUL_HEADERS_ID.productBreakdownGiftingCollection,
      preview
    })) || { productsBreakdown: { items: [] } }
    return productsBreakdown
  }

  const { productsBreakdown } =
    (await fetchContentfulProductsBreakdownByProductCategory({
      productCategory,
      preview
    })) || { productsBreakdown: { items: [] } }

  return productsBreakdown?.items[0]
}

async function fetchProductCareCopy(productType: string, preview = false) {
  const productCareCopy = await fetchContenfulProductCareCopy({
    productType,
    preview
  })
  return getValueFromObject<ContentfulDocument | null>(
    productCareCopy,
    'productCareCopy.items[0].copy.json',
    null
  )
}

async function fetchProductContent({
  productTypes,
  handle,
  isPreview = false,
  contentType
}: {
  productTypes: string[]
  handle: string
  isPreview?: boolean
  contentType: string
}) {
  let productTypesContent = null

  for (let i = 0; i < productTypes.length; i++) {
    const productType = productTypes[i]

    try {
      const response = await searchDataByAttribute<
        BenefitsProps | StatementSectionProps
      >(
        {
          content_type: contentType,
          'fields.productTypes': productType
        },
        isPreview
      )

      productTypesContent =
        Object.keys(response).length > 0
          ? (response as BenefitsProps | StatementSectionProps)
          : null

      if (i === 0 && response) {
        break
      }
    } catch (error) {
      handleProductsBuildError({
        errorLevel: 'error',
        properties: {
          handle,
          message: `Failed to fetch benefits for ${handle}`,
          error
        }
      })
      productTypesContent = null
    }
  }

  return productTypesContent
}

type CMSInput = {
  productType: string
  productCategory: string
  productTypes: string[]
  discountProductTypes?: string[]
  productHandle: string
  preview?: boolean
}

export type ProductPageCMSData = {
  productsBreakdown: ProductsBreakdownProps | null
  careCopy: ContentfulDocument | null
  productBenefits: BenefitsProps | StatementSectionProps | null
  productStatementSection: BenefitsProps | StatementSectionProps | null
  discountByProductType: DiscountByProductType
}

export async function fetchProductPageCMSData({
  productType,
  productCategory,
  productTypes,
  discountProductTypes = productTypes,
  productHandle,
  preview = false
}: CMSInput): Promise<ProductPageCMSData> {
  const [
    productsBreakdown,
    careCopy,
    productBenefits,
    productStatementSection,
    discountByProductType
  ] = await Promise.all([
    getProductsBreakdown(productType, productCategory, preview),
    fetchProductCareCopy(productType, preview),
    fetchProductContent({
      productTypes: productTypes,
      handle: productHandle,
      contentType: 'benefits',
      isPreview: preview
    }),
    fetchProductContent({
      productTypes: productTypes,
      handle: productHandle,
      contentType: 'statementSection',
      isPreview: preview
    }),
    fetchProductDiscount(discountProductTypes, preview)
  ])

  return {
    productsBreakdown:
      (productsBreakdown as unknown as ProductPageCMSData['productsBreakdown']) ||
      null,
    careCopy: careCopy || null,
    productBenefits: productBenefits || null,
    productStatementSection: productStatementSection || null,
    discountByProductType
  }
}
